<template>
  <Teleport v-if="isTeleportReady" to="#teleported">
    <div
      v-show="isMobileNavigationOpen"
      ref="containerRef"
      class="fixed inset-0 z-1000"
    >
      <div class="h-full overflow-y-scroll overscroll-contain bg-white pb-8">
        <div class="flex flex-col md:flex-row">
          <div
            class="flex flex-col p-4 md:w-1/2 md:border-r md:border-neutral-200 md:pr-8"
          >
            <div
              v-for="(item, index) in navigation"
              :key="index"
              class="space-y-1"
            >
              <InertiaLink
                v-if="!item.dropdown"
                :href="item.href[accessControl.actorType]"
                class="inline-flex items-center p-3 font-semibold hover:text-current"
                prefetch="hover"
              >
                <component
                  :is="item.icon"
                  class="mr-4 h-6 w-6 stroke-[1.5px]"
                />
                {{ item.name }}
              </InertiaLink>

              <Disclosure v-else>
                <template
                  #activator="{ toggle, isActive, ariaExpanded, ariaControls }"
                >
                  <button
                    :aria-expanded="ariaExpanded"
                    :aria-controls="ariaControls"
                    @click="toggle"
                    class="inline-flex w-full items-center justify-between p-3 font-semibold hover:text-current"
                  >
                    <span class="inline-flex items-center">
                      <component
                        :is="item.icon"
                        class="mr-4 h-6 w-6 stroke-[1.5px]"
                      />
                      {{ item.name }}
                    </span>
                    <ChevronRightIcon
                      class="ml-2 w-2 shrink-0 transition-transform"
                      :class="[isActive ? 'rotate-270' : 'rotate-90']"
                    />
                  </button>
                </template>
                <template #content>
                  <div class="flex flex-col space-y-1">
                    <InertiaLink
                      v-for="dropdownElement in item.dropdown"
                      :key="dropdownElement.name"
                      :href="dropdownElement.href[accessControl.actorType]"
                      class="inline-flex items-center p-3 font-semibold hover:text-current"
                      prefetch="hover"
                    >
                      <component
                        :is="dropdownElement.icon"
                        class="mr-4 h-6 w-6 stroke-[1.5px]"
                      />
                      {{ dropdownElement.name }}
                    </InertiaLink>
                  </div>
                </template>
              </Disclosure>
            </div>
            <div v-if="isUserLoggedIn" class="mt-4 flex flex-col gap-y-2">
              <BaseButton
                v-if="ASK_ENABLED"
                color="purple"
                size="large"
                is-block
                href="/ask/new"
                is-inertia-link
              >
                Ask
              </BaseButton>
              <BaseButton
                color="primary"
                size="large"
                is-block
                href="/search"
                is-inertia-link
                :prefetch="['mount', 'hover']"
              >
                Browse mentors
              </BaseButton>
            </div>
          </div>
          <div v-if="isUserLoggedIn" class="p-4 md:w-1/2 md:pl-8">
            <div class="text-xs font-semibold text-neutral-500 uppercase">
              Account
            </div>
            <InertiaLink
              :href="currentUser.profileUrl"
              class="mt-2 flex items-center justify-between rounded-lg p-3 hover:bg-neutral-50"
            >
              <div class="flex min-w-0 items-center">
                <Avatar
                  :src="currentUser.avatarUrl"
                  disable-lazy-loading
                  size="lg"
                  class="mr-3 shrink-0"
                />
                <div class="min-w-0">
                  <div class="truncate font-semibold">
                    {{ currentUser.firstName }} {{ currentUser.lastName }}
                  </div>
                  <div class="text-xs text-neutral-400">view profile</div>
                </div>
              </div>
              <ChevronRightIcon class="ml-2 size-4 shrink-0 text-neutral-400" />
            </InertiaLink>
            <FreeToTalkToggle class="mt-1 px-3" />
            <div class="mt-3 flex flex-col space-y-1">
              <component
                v-for="(item, index) in userDropdownNavigation[
                  accessControl.role
                ]"
                v-bind="{ ...item.attrs, ...item.actions }"
                :is="item.as"
                :key="index"
                :href="item.isDisabled ? 'javascript:void(0)' : item.href"
                class="p-2 text-left text-neutral-700 hover:text-current"
                :class="{
                  'cursor-not-allowed': item.isDisabled,
                }"
              >
                <div class="inline-flex items-center">
                  <component
                    :is="item.icon"
                    class="mr-3 h-6 w-6 stroke-[1.5px]"
                    :class="item.iconClasses"
                  />
                  <span class="font-medium">{{ item.name }}</span>
                  <span
                    v-if="item.isNew"
                    class="bg-success-100 text-success-600 ml-1 rounded-md px-1 py-0.5 text-xs"
                  >
                    New
                  </span>
                </div>
                <span
                  v-if="item.description"
                  class="mt-1 flex text-xs"
                  :class="{ 'text-neutral-400': item.isDisabled }"
                >
                  {{ item.description }}
                </span>
              </component>
            </div>
          </div>
          <div
            v-else
            class="flex p-4 sm:justify-center md:mt-0 md:w-1/2 md:items-center md:pl-8"
          >
            <div
              class="xs:flex-row xs:space-x-2 xs:space-y-0 flex w-full flex-col space-y-2 md:grow md:flex-col md:space-y-4 md:space-x-0"
            >
              <BaseButton size="small" href="/search" is-block is-inertia-link>
                Browse mentors
              </BaseButton>
              <BaseButton
                color="outline-secondary"
                href="/mentees/sign_up?flow=classic"
                is-block
              >
                Sign up
              </BaseButton>
              <BaseButton color="primary" href="/users/sign_in" is-block>
                Log in
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import BaseButton from '../BaseButton.vue'
import ChevronRightIcon from '~icons/lucide/chevron-right'
import Avatar from '../avatar/Avatar.vue'
import FreeToTalkToggle from '@/modules/instant-sessions/components/FreeToTalkToggle.vue'
import Disclosure from '@/shared/components/Disclosure.vue'
import { onMounted, ref, useTemplateRef, watch } from 'vue'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import { useFeatureFlags } from '@/shared/composables/useFeatureFlags'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  navigation: {
    type: Array,
    required: true,
  },
  userDropdownNavigation: {
    type: Object,
    default: () => ({}),
  },
})

const { isUserLoggedIn, accessControl } = usePermissionSystem()
const { currentUser } = useCurrentUser()
const { ASK_ENABLED } = useFeatureFlags()

const isMobileNavigationOpen = ref(false)
const isTeleportReady = ref(false)
const containerRef = useTemplateRef('containerRef')

onMounted(() => {
  isTeleportReady.value = true
})

watch(
  () => props.isOpen,
  (value) => {
    if (value) {
      lockBody()
      setContainerPosition()
      isMobileNavigationOpen.value = true
    } else {
      unlockBody()
      isMobileNavigationOpen.value = false
    }
  },
)

const lockBody = () => {
  document.body.classList.add('overflow-hidden!')
}

const unlockBody = () => {
  document.body.classList.remove('overflow-hidden!')
}

const setContainerPosition = () => {
  const main = document.getElementsByTagName('main')[0]
  // main.offsetTop is the distance from the top of the page to the top of the main element
  // (we want to keep everything above main visible while opening the mobile navigation)
  // 100dvh, not 100vh: on iOS Safari 100vh is the height with the toolbar hidden,
  // so the last items (Give feedback, Log out) sat under the toolbar.
  const viewportHeight = CSS.supports('height', '100dvh') ? '100dvh' : '100vh'
  containerRef.value.style.height = `calc(${viewportHeight} - ${main.offsetTop}px)`
  containerRef.value.style.marginTop = `${main.offsetTop}px`
}
</script>
